import { TextField, Autocomplete, FormControlLabel, Checkbox, InputAdornment, Grid, Box } from '@mui/material'
import { useMemo, useState, useEffect } from 'react'
import { debounce } from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import { SearchIcon, CloseIcon } from './Icons'
import { useSession } from './Providers'
import StudentSearch from './StudentSearch'
import StaffSearch from './StaffSearch'

// `name` matches the free-text name filters the API exposes; `personId` is for id-based endpoints
const personValue = (record, valueField) =>
  valueField === 'name' ? `${record?.person?.firstName ?? ''} ${record?.person?.lastName ?? ''}`.trim() : record?.[valueField]

const Filters = ({
  filtersConfig = [],
  values = {},
  onChange,
  optionsMap = {},
  api,
  defaultQuery = {},
  gridContainerProps = {},
}) => {
  const dispatch = useDispatch()
  const { district } = useSession()
  const query = api?.querySelector ? useSelector(api.querySelector) : values
  const [localTextValues, setLocalTextValues] = useState({})
  const allStudents = useSelector(state => state.lookup.allStudents?.rows)
  const allStaff = useSelector(state => state.lookup.allStaff?.rows)

  useEffect(() => {
    if (!api) return
    if (api.setQuery) dispatch(api.setQuery(defaultQuery))
    if (api.search) dispatch(api.search(defaultQuery))
    setLocalTextValues({})
  }, [district?.id, JSON.stringify(defaultQuery)])

  const delayedSearch = useMemo(() => {
    return debounce((key, value) => {
      handleFilter(key, value)
    }, 500)
  }, [JSON.stringify(query)])

  const filterNullValues = obj => {
    let temp = {}
    Object.keys(obj).forEach(key => {
      if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '' && key !== 'offset' && key !== 'showArchived') {
        temp[key] = obj[key]
      }
    })
    return temp
  }

  const applyQuery = base => {
    const newQuery = filterNullValues(base)

    if (onChange) onChange(newQuery)
    if (api?.setQuery) dispatch(api.setQuery(newQuery))
    if (api?.search) dispatch(api.search(newQuery))
  }

  const handleChange = (key, value) => {
    const base = { ...query }
    if (value === '' || value === null || value === undefined) {
      delete base[key]
    } else {
      base[key] = value
    }
    // a filter declaring `dependsOn: key` is scoped by this one, so its selection no longer applies
    filtersConfig.forEach(filter => {
      if (filter.dependsOn === key) delete base[filter.key]
    })

    applyQuery(base)
  }

  const handleFilter = (key, value) => {
    if (api?.setQuery) {
      dispatch(api.setQuery(filterNullValues({ ...query, [key]: value })))
    }
    
    if (api?.search) {
      const newQuery = filterNullValues({ ...query })
      
      if (typeof value === 'string' && value.trim().length > 2) {
        newQuery[key] = value
        dispatch(api.search(newQuery))
      } else if (!value || value === '') {
        delete newQuery[key]
        dispatch(api.search(newQuery))
      }
    }
  }

  const getOptions = (filter) => {
    if (filter.options) return filter.options;
    return optionsMap?.[filter.key] || [];
  };

  return (
    <Grid container spacing={1} sx={{ p: 2, width: '100%', flexWrap: 'wrap', ...gridContainerProps.sx }} {...gridContainerProps}>
      {filtersConfig.map((filter) => {
        const { type, key, label, gridProps = {}, size = 'small', ...rest } = filter
        const options = getOptions(filter)
        const value = query?.[key] ?? ''

        const commonProps = {
          key,
          label,
          fullWidth: true,
          size,
          sx: { width: '100%' },
          ...rest,
        }

        let input = null
        switch (type) {
          case 'text': {
            const textValue = localTextValues[key] !== undefined ? localTextValues[key] : value
            input = (
              <TextField
                {...commonProps}
                value={textValue}
                onChange={e => {
                  const newValue = e.target.value
                  setLocalTextValues(prev => ({ ...prev, [key]: newValue }))
                  if (api) {
                    delayedSearch(key, newValue)
                  } else {
                    handleChange(key, newValue)
                  }
                }}
                InputProps={api ? {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <CloseIcon
                      onClick={() => {
                        setLocalTextValues(prev => ({ ...prev, [key]: '' }))
                        handleFilter(key, '')
                      }}
                      sx={{ '&:hover': { cursor: 'pointer' }, fontSize: 'small' }}
                    />
                  ),
                } : {}}
              />
            )
            break
          }
          case 'autocomplete': {
            const { minClearLength = 0, clearableWhenKey, ...autocompleteRest } = rest
            const watchedValue = clearableWhenKey ? String(query?.[clearableWhenKey] ?? '') : ''
            const isClearable = minClearLength === 0 || watchedValue.length >= minClearLength
            input = (
              <Autocomplete
                key={key}
                label={label}
                fullWidth
                size={size}
                sx={{ width: '100%' }}
                {...autocompleteRest}
                options={options}
                getOptionLabel={option => option.label ?? option.name ?? option}
                value={options.find(opt => opt.value === value || opt.id === value) || null}
                onChange={(_, newValue) => handleChange(key, newValue?.value ?? newValue?.id ?? '')}
                disableClearable={!isClearable}
                renderInput={params => (
                  <TextField {...params} label={label} size={size} />
                )}
              />
            )
            break
          }
          case 'student': {
            const { valueField = 'personId', scopeBy = {} } = rest
            input = (
              <StudentSearch
                label={label}
                size={size}
                value={(allStudents ?? []).find(student => personValue(student, valueField) === value) || null}
                gradeLevelId={scopeBy.gradeLevelId ? query?.[scopeBy.gradeLevelId] ?? null : null}
                gradeSectionId={scopeBy.gradeSectionId ? query?.[scopeBy.gradeSectionId] ?? null : null}
                onSelect={student => handleChange(key, student ? personValue(student, valueField) : '')}
              />
            )
            break
          }
          case 'staff': {
            const { valueField = 'personId' } = rest
            input = (
              <StaffSearch
                label={label}
                size={size}
                value={(allStaff ?? []).find(staff => personValue(staff, valueField) === value) || null}
                onSelect={staff => handleChange(key, staff ? personValue(staff, valueField) : '')}
              />
            )
            break
          }
          case 'custom': {
            input = filter.render?.({ value, query, onChange: next => handleChange(key, next), applyQuery })
            break
          }
          case 'checkbox': {
            const { omitWhenFalse } = rest
            input = (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '5px', pl: 1 }}>
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={Boolean(value)}
                      onChange={e => handleChange(key, omitWhenFalse && !e.target.checked ? null : e.target.checked)}
                      size="small"
                    />
                  }
                  label={label}
                />
              </Box>
            )
            break
          }
          default:
            input = null
        }
        return (
          <Grid item key={key} {...gridProps}>
            {input}
          </Grid>
        )
      })}
    </Grid>
  )
}

export default Filters
